import React, { useState, useEffect, useRef, useCallback } from "react";
import apiPath from "../../constants/apiPath";
import signinLogo from "../../assets/images/logo-for-logins.png";

// ── Helpers ──────────────────────────────────────────────────────────────────
const getWeekDates = () => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: window._fhTimezone || "Australia/Sydney" }));
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" }));
  }
  return dates;
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BRAND = "#1919ce";
const YELLOW = "#fcdc08";
const BG = "#0d0d1a";
const SURFACE = "rgba(255,255,255,0.05)";
const BORDER = "rgba(255,255,255,0.09)";

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.login}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      const user = data?.data?.user || data?.user;
      const token = user?.token || user?.access_token;
      if (token) {
        localStorage.setItem("accessToken", token);
        if (user?.refresh_token) localStorage.setItem("refresh_token", user.refresh_token);
        onLogin();
      } else {
        setError(data?.message || "Invalid email or password.");
      }
    } catch {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  const S = {
    page: { minHeight: "100vh", background: BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", fontFamily: "'Inter',-apple-system,sans-serif" },
    card: { width: "100%", maxWidth: 380, background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 18, padding: "36px 28px", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" },
    logo: { display: "block", height: 44, margin: "0 auto 28px", objectFit: "contain" },
    title: { color: "#fff", fontSize: 20, fontWeight: 800, textAlign: "center", marginBottom: 4 },
    subtitle: { color: "#666", fontSize: 13, textAlign: "center", marginBottom: 28 },
    label: { display: "block", color: "#999", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 6 },
    input: { width: "100%", background: "rgba(255,255,255,0.06)", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "13px 14px", color: "#fff", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 16 },
    btn: { width: "100%", padding: "14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${BRAND},#003d8f)`, color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: "0.08em", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginTop: 4, boxShadow: "0 4px 20px rgba(25,25,206,0.4)" },
    error: { color: "#ef4444", fontSize: 13, textAlign: "center", marginTop: 12, padding: "8px 12px", background: "rgba(239,68,68,0.1)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.2)" },
    divider: { textAlign: "center", color: "#444", fontSize: 11, marginTop: 24, letterSpacing: "0.05em" },
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <img src={signinLogo} alt="FusionHub" style={S.logo} />
        <div style={S.title}>Control Panel</div>
        <div style={S.subtitle}>Sign in with your FusionHub account</div>
        <form onSubmit={handleSubmit} autoComplete="on">
          <label style={S.label}>EMAIL</label>
          <input style={S.input} type="email" placeholder="you@gym.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
          <label style={S.label}>PASSWORD</label>
          <input style={S.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          <button type="submit" style={S.btn} disabled={loading}>{loading ? "SIGNING IN…" : "SIGN IN"}</button>
          {error && <div style={S.error}>{error}</div>}
        </form>
        <div style={S.divider}>FUSIONHUB GYM CONTROL</div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function MobileControl() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("accessToken"));
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return <ControlPanel onLogout={() => { localStorage.removeItem("accessToken"); setAuthed(false); }} />;
}

// ── Control Panel ─────────────────────────────────────────────────────────────
function ControlPanel({ onLogout }) {
  const DAYS = getWeekDates();
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" });
  const todayIdx = DAYS.indexOf(todayStr);
  const defaultIdx = todayIdx >= 0 ? todayIdx : 0;

  const [activeIdx, setActiveIdx] = useState(defaultIdx);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [mode, setMode] = useState("hero");
  const [workoutActive, setWorkoutActive] = useState(false);
  const [workoutPaused, setWorkoutPaused] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [toast, setToast] = useState(null);
  const [bellEnabled, setBellEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fh_bell_enabled") ?? "true"); } catch { return true; }
  });

  const reconnectRef = useRef(null);
  const activeDay = DAYS[activeIdx];

  const currentDay = weekData?.full_week
    ? (() => {
        const d = weekData.full_week.dates?.[activeDay] || weekData.full_week[activeDay];
        return d || { data: {}, media: {} };
      })()
    : { data: {}, media: {} };

  const media = currentDay.media || {};

  const playBell = (type = "transition") => {
    if (!bellEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      if (type === "complete") {
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.18);
        gain.gain.setValueAtTime(0.4, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.7);
      } else {
        osc.frequency.setValueAtTime(1047, ctx.currentTime);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
      }
      ctx.close();
    } catch {}
  };

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // ── WebSocket ───────────────────────────────────────────────────────────────
  const connectWS = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    const base = apiPath.baseURL.replace("/api/v1/", "/").replace("http://", "ws://").replace("https://", "wss://");
    const ws = new WebSocket(`${base}ws/preview/?token=${encodeURIComponent(token)}`);
    ws.onopen = () => { setConnected(true); setSocket(ws); };
    ws.onclose = () => {
      setConnected(false); setSocket(null);
      reconnectRef.current = setTimeout(connectWS, 3000);
    };
    ws.onerror = () => ws.close();
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.day && data.day !== activeDay) return;
        if (data.type === "workout_complete") {
          setWorkoutActive(false); setWorkoutPaused(false);
          playBell("complete");
        } else if (data.type === "workout_stopped") {
          setWorkoutActive(false); setWorkoutPaused(false);
        } else if (data.type === "show_transition") {
          playBell("transition");
        } else if (data.type === "show_countdown" || data.type === "timer_update") {
          setWorkoutActive(true); setWorkoutPaused(false); setMode("workout");
        } else if (data.type === "pause_workout" || data.type === "workout_paused") {
          setWorkoutPaused(true);
        } else if (data.type === "resume_workout" || data.type === "workout_resumed") {
          setWorkoutPaused(false);
        } else if (data.type === "show_pre_hype") { setMode("pre_hype"); }
        else if (data.type === "show_warmup") { setMode("warmup"); }
        else if (data.type === "show_hero") { setMode("hero"); }
        else if (data.type === "show_table") { setMode("table"); }
      } catch {}
    };
    return ws;
  }, [activeDay, bellEnabled]);

  useEffect(() => {
    const ws = connectWS();
    return () => { clearTimeout(reconnectRef.current); ws?.close(); };
  }, [connectWS]);

  // ── Fetch plan ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${apiPath.baseURL}${apiPath.getWeeklyPlan}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.status === 200) { setWeekData(d); try { localStorage.setItem("fh_programme_cache", JSON.stringify(d)); } catch {} } })
      .catch(() => {
        const cached = localStorage.getItem("fh_programme_cache");
        if (cached) try { setWeekData(JSON.parse(cached)); showToast("Offline — using cached plan", "warn"); } catch {}
      });
  }, []);

  // ── Send command ────────────────────────────────────────────────────────────
  const send = (type, payload = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      showToast("Not connected", "error");
      return false;
    }
    socket.send(JSON.stringify({ type, day: activeDay, payload: { message: type, ...payload } }));
    return true;
  };

  // ── Button handlers — identical logic to dashboard ─────────────────────────
  const handlePreview = () => {
    if (mode === "table") { setMode("hero"); send("show_hero"); }
    else { setMode("table"); send("show_table", { day: activeDay, clear_countdown: true }); }
  };

  const handleRestart = () => {
    send("stop_workout", { day: activeDay, stop_all: true });
    send("show_hero", { day: activeDay, reset_all: true });
    setMode("hero"); setWorkoutActive(false); setWorkoutPaused(false);
    showToast("Restarted");
  };

  const handlePreHype = () => {
    if (mode === "pre_hype") { setMode("hero"); send("show_hero"); }
    else {
      setMode("pre_hype");
      const v = media.pre_hype_video;
      send("show_pre_hype", v ? { video_url: v } : {});
    }
  };

  const handleWarmup = () => {
    if (mode === "warmup") { setMode("hero"); send("show_hero"); }
    else {
      setMode("warmup");
      const v = media.warmup_video;
      send("show_warmup", v ? { video_url: v } : {});
    }
  };

  const handleStartStop = () => {
    const allBlocks = Object.keys(currentDay.data || {}).filter(k => k.endsWith("_BLOCK")).sort();
    if (workoutActive) {
      if (workoutPaused) {
        allBlocks.forEach((bk, i) => {
          const bd = currentDay.data[bk] || {};
          const dur = bd.duration ? (parseInt(bd.duration.split(":")[0]) * 60 + parseInt(bd.duration.split(":")[1] || 0)) : 600;
          send("resume_workout", { day: activeDay, resume_all: true, current_block: bk, current_block_index: i, all_blocks: [{ block_name: bk, duration: dur, timeSegments: bd.timeSegments || [], total_seconds: dur }] });
        });
        setWorkoutPaused(false);
      } else {
        send("pause_workout", { day: activeDay, pause_all: true });
        setWorkoutPaused(true);
      }
    } else {
      if (allBlocks.length === 0) { showToast("No blocks loaded — check plan", "warn"); return; }
      const allBlocksData = allBlocks.map(k => {
        const bd = currentDay.data[k] || {};
        const dur = bd.duration ? (parseInt(bd.duration.split(":")[0]) * 60 + parseInt(bd.duration.split(":")[1] || 0)) : 600;
        return { block_name: k, duration: dur, timeSegments: bd.timeSegments || [], total_seconds: dur, block_transition: bd.block_transition || 0 };
      });
      allBlocks.forEach((bk, i) => {
        const bd = currentDay.data[bk] || {};
        const dur = bd.duration ? (parseInt(bd.duration.split(":")[0]) * 60 + parseInt(bd.duration.split(":")[1] || 0)) : 600;
        send("start_workout", { day: activeDay, all_blocks: allBlocksData, block_transition: bd.block_transition || 0, current_block: bk, current_block_index: i, total_blocks: allBlocks.length, current_repetition: 1, max_repetitions: allBlocks.length, completion_message: currentDay.completion_settings?.message || "You've just completed!" });
      });
      setWorkoutActive(true); setWorkoutPaused(false);
    }
  };

  const handleCooldown = () => {
    if (mode === "cooldown") { setMode("hero"); send("show_hero"); }
    else { setMode("cooldown"); send("show_hero"); showToast("Cooldown triggered"); }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const S = {
    page: { minHeight: "100vh", background: BG, color: "#e0e0e0", fontFamily: "'Inter',-apple-system,sans-serif", paddingBottom: 40 },
    header: { background: "rgba(13,13,26,0.97)", borderBottom: `1px solid rgba(252,220,8,0.18)`, padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, height: 54 },
    logoImg: { height: 26, objectFit: "contain" },
    dot: (on) => ({ width: 8, height: 8, borderRadius: "50%", background: on ? "#22c55e" : "#ef4444", boxShadow: on ? "0 0 6px #22c55e" : "none", flexShrink: 0 }),
    connRow: { display: "flex", alignItems: "center", gap: 8 },
    connLabel: { fontSize: 11, color: "#888", letterSpacing: "0.06em" },
    logoutBtn: { background: "none", border: `1px solid ${BORDER}`, borderRadius: 7, padding: "5px 10px", color: "#666", fontSize: 11, fontWeight: 700, cursor: "pointer" },
    dayScroll: { display: "flex", overflowX: "auto", padding: "12px 12px 0", gap: 8, scrollbarWidth: "none" },
    dayBtn: (active) => ({ flexShrink: 0, padding: "8px 14px", borderRadius: 10, border: active ? `1px solid ${BRAND}` : `1px solid ${BORDER}`, background: active ? "rgba(25,25,206,0.2)" : SURFACE, color: active ? "#fff" : "#888", fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap" }),
    statusBar: { margin: "12px 16px", padding: "10px 14px", borderRadius: 10, background: SURFACE, border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 8 },
    modeBadge: { padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", background: mode === "workout" ? "rgba(25,25,206,0.3)" : mode === "hero" ? "rgba(252,220,8,0.15)" : "rgba(255,255,255,0.08)", color: mode === "workout" ? "#80a0ff" : mode === "hero" ? YELLOW : "#ccc", border: `1px solid ${mode === "workout" ? "rgba(25,25,206,0.4)" : mode === "hero" ? "rgba(252,220,8,0.2)" : BORDER}` },
    btnGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "16px" },
    btn: (active, accent) => ({ padding: "20px 8px", borderRadius: 14, border: active ? `1px solid ${accent || BRAND}` : `1px solid ${BORDER}`, background: active ? `linear-gradient(135deg,${accent || BRAND},${accent ? accent + "bb" : "#003d8f"})` : SURFACE, color: "#fff", fontSize: 12, fontWeight: 800, letterSpacing: "0.07em", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, boxShadow: active ? `0 4px 20px ${accent || BRAND}55` : "none", transition: "all 0.15s", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }),
    startBtn: { gridColumn: "1 / -1", padding: "22px 8px", borderRadius: 14, border: workoutActive ? (workoutPaused ? "1px solid #f59e0b" : "1px solid rgba(239,68,68,0.5)") : "1px solid rgba(25,206,100,0.5)", background: workoutActive ? (workoutPaused ? "linear-gradient(135deg,#b45309,#92400e)" : "linear-gradient(135deg,#dc2626,#991b1b)") : "linear-gradient(135deg,#15803d,#166534)", color: "#fff", fontSize: 16, fontWeight: 900, letterSpacing: "0.1em", cursor: "pointer", boxShadow: workoutActive ? "0 4px 20px rgba(220,38,38,0.3)" : "0 4px 20px rgba(21,128,61,0.3)", transition: "all 0.15s" },
    bellRow: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 16px 4px", padding: "8px 14px", borderRadius: 10, background: SURFACE, border: `1px solid ${BORDER}` },
    bellLabel: { color: "#888", fontSize: 12 },
    bellBtn: (on) => ({ padding: "4px 12px", borderRadius: 6, border: "none", background: on ? "rgba(252,220,8,0.15)" : "rgba(255,255,255,0.05)", color: on ? YELLOW : "#666", fontSize: 11, fontWeight: 700, cursor: "pointer" }),
    blockList: { padding: "0 16px 16px" },
    blockHeader: { color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8, textTransform: "uppercase" },
    blockItem: { padding: "10px 14px", borderRadius: 10, background: SURFACE, border: `1px solid ${BORDER}`, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" },
    toastWrap: { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, pointerEvents: "none" },
    toastEl: (type) => ({ padding: "10px 20px", borderRadius: 10, fontWeight: 700, fontSize: 13, color: "#fff", background: type === "error" ? "rgba(220,38,38,0.95)" : type === "warn" ? "rgba(180,83,9,0.95)" : "rgba(21,128,61,0.95)", boxShadow: "0 4px 20px rgba(0,0,0,0.4)", whiteSpace: "nowrap" }),
  };

  const blocks = Object.keys(currentDay.data || {}).filter(k => k.endsWith("_BLOCK")).sort();
  const startLabel = workoutActive ? (workoutPaused ? "⏵  RESUME" : "⏸  PAUSE") : "▶  START WORKOUT";

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <img src={signinLogo} alt="FusionHub" style={S.logoImg} />
        <div style={S.connRow}>
          <div style={S.dot(connected)} />
          <span style={S.connLabel}>{connected ? "LIVE" : "Connecting…"}</span>
          <button style={S.logoutBtn} onClick={onLogout}>SIGN OUT</button>
        </div>
      </div>

      {/* Day selector */}
      <div style={S.dayScroll}>
        {DAYS.map((d, i) => {
          const date = new Date(d + "T00:00:00");
          const label = `${DAY_LABELS[i]} ${date.getDate()}`;
          return (
            <button key={d} style={S.dayBtn(i === activeIdx)} onClick={() => setActiveIdx(i)}>
              {label}{d === todayStr ? " ·" : ""}
            </button>
          );
        })}
      </div>

      {/* Status bar */}
      <div style={S.statusBar}>
        <span style={S.modeBadge}>{mode.replace(/_/g, " ").toUpperCase()}</span>
        <span style={{ color: "#666", fontSize: 12 }}>{blocks.length} block{blocks.length !== 1 ? "s" : ""}</span>
        {workoutActive && !workoutPaused && <span style={{ marginLeft: "auto", color: "#22c55e", fontSize: 12, fontWeight: 700 }}>● RUNNING</span>}
        {workoutPaused && <span style={{ marginLeft: "auto", color: "#f59e0b", fontSize: 12, fontWeight: 700 }}>⏸ PAUSED</span>}
      </div>

      {/* Bell toggle */}
      <div style={S.bellRow}>
        <span style={S.bellLabel}>Block alert bells</span>
        <button style={S.bellBtn(bellEnabled)} onClick={() => {
          const next = !bellEnabled;
          setBellEnabled(next);
          try { localStorage.setItem("fh_bell_enabled", JSON.stringify(next)); } catch {}
        }}>{bellEnabled ? "ON" : "OFF"}</button>
      </div>

      {/* Control buttons */}
      <div style={S.btnGrid}>
        <button style={S.btn(mode === "table")} onClick={handlePreview}>
          <span style={{ fontSize: 22 }}>👁</span>
          {mode === "table" ? "RESET" : "PREVIEW"}
        </button>
        <button style={S.btn(false)} onClick={handleRestart}>
          <span style={{ fontSize: 22 }}>↺</span>
          RESTART
        </button>
        <button style={S.btn(mode === "pre_hype", "#7c3aed")} onClick={handlePreHype}>
          <span style={{ fontSize: 22 }}>⚡</span>
          {mode === "pre_hype" ? "STOP" : "PRE-HYPE"}
        </button>
        <button style={S.btn(mode === "warmup", "#0891b2")} onClick={handleWarmup}>
          <span style={{ fontSize: 22 }}>🔥</span>
          {mode === "warmup" ? "STOP" : "WARM-UP"}
        </button>
        <button style={S.btn(mode === "cooldown", "#0f766e")} onClick={handleCooldown}>
          <span style={{ fontSize: 22 }}>❄</span>
          {mode === "cooldown" ? "STOP" : "COOLDOWN"}
        </button>
        <button style={S.btn(false)} onClick={() => window.open("/dashboard", "_blank")}>
          <span style={{ fontSize: 22 }}>⚙</span>
          DASHBOARD
        </button>

        <button style={S.startBtn} onClick={handleStartStop}>
          {startLabel}
        </button>
      </div>

      {/* Block list */}
      {blocks.length > 0 && (
        <div style={S.blockList}>
          <div style={S.blockHeader}>Today's Blocks</div>
          {blocks.map(bk => {
            const bd = currentDay.data[bk] || {};
            return (
              <div key={bk} style={S.blockItem}>
                <span style={{ color: YELLOW, fontWeight: 700, fontSize: 13 }}>{bk.replace("_BLOCK", "")} BLOCK</span>
                <span style={{ color: "#888", fontSize: 12 }}>{bd.title || ""}</span>
                <span style={{ color: "#555", fontSize: 12 }}>{bd.duration || "--"}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={S.toastWrap}>
          <div style={S.toastEl(toast.type)}>{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
